import { 
  Users, 
  BookOpen, 
  LayoutDashboard, 
  MessageSquare, 
  Settings, 
  ShieldCheck, 
  LogOut,
  Menu,
  Bell,
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  GraduationCap,
  GitBranch,
  FileText,
  Mail,
  CreditCard,
  PenTool,
  Copy,
  HelpCircle,
  LifeBuoy,
  Award,
  ClipboardList,
  History,
  Wrench,
  AlertCircle,
  type LucideIcon
} from "@/components/icons";
import SupportTicketDialog from "@/components/support-ticket-dialog";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";

import soulLogo from "@/assets/soul-living-logo-long.png";
import soulMark from "@/assets/soul-living-mark.png";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { HeaderSearch } from "@/components/header-search";
import { useAuth } from "@/lib/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotificationSSE } from "@/hooks/use-notification-sse";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface NavItem {
  name: string;
  href: string;
  navigateTo?: string;
  icon: LucideIcon;
  roles: string[];
  showAlways: boolean;
  showForAudit?: boolean;
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [location, setLocation] = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();

  async function markAlertsRead() {
    await fetch("/api/admin/system-alerts/mark-viewed", { method: "POST", credentials: "include" });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/system-alerts/unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/system-alerts", "recent-unread"] });
  }

  useNotificationSSE(!!user);

  const { data: auditAccess } = useQuery({
    queryKey: ["/api/user/audit-access"],
    queryFn: async () => {
      const res = await fetch("/api/user/audit-access", { credentials: "include" });
      if (!res.ok) return { hasAuditAccess: false };
      return res.json();
    },
    enabled: !!user,
  });
  
  const { data: unreadMessages } = useQuery({
    queryKey: ["/api/messages/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/messages/unread-count", { credentials: "include" });
      // Throw on 401 so TanStack Query enters an error state and stops the
      // refetch interval — prevents log spam when a session expires but the
      // Clerk client still has a cached user object.
      if (res.status === 401) throw new Error("unauthenticated");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    enabled: !!user,
    retry: false,
    refetchInterval: 120000,
  });

  const { data: unreadSupport } = useQuery({
    queryKey: ["/api/support/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/support/unread-count", { credentials: "include" });
      if (res.status === 401) throw new Error("unauthenticated");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    enabled: !!user,
    retry: false,
    refetchInterval: 120000,
  });

  const isStaff = !!user && (user.role === "admin" || user.role === "content_admin" || user.role === "program_admin" || user.role === "advisor");

  const { data: alertsUnread } = useQuery({
    queryKey: ["/api/admin/system-alerts/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/admin/system-alerts/unread-count", { credentials: "include" });
      if (res.status === 401) throw new Error("unauthenticated");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    enabled: isStaff,
    retry: false,
    refetchInterval: 300000,
  });

  const { data: recentAlerts } = useQuery<{ alerts: { id: string; alertKey: string; message: string; createdAt: string }[] }>({
    queryKey: ["/api/admin/system-alerts", "recent-unread"],
    queryFn: async () => {
      const res = await fetch("/api/admin/system-alerts?page=1&limit=5&unreadOnly=true", { credentials: "include" });
      if (!res.ok) return { alerts: [] };
      return res.json();
    },
    enabled: isStaff,
    retry: false,
    refetchInterval: 300000,
  });

  const unreadCount = unreadMessages?.count || 0;
  const supportUnread = unreadSupport?.count || 0;
  const alertsUnreadCount = alertsUnread?.count || 0;
  const bellTotal = supportUnread + unreadCount + alertsUnreadCount;
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const hasAuditAccess = user?.role === "admin" || auditAccess?.hasAuditAccess;

  const handleLogout = async () => {
    await logout();
  };

  const handleNavClick = () => {
    setIsMobileOpen(false);
  };

  const allNavigation: NavItem[] = [
    { name: "My Programs", href: "/", icon: BookOpen, roles: ["participant", "advisor", "program_admin", "content_admin", "admin"], showAlways: true },
    // Hidden from participants for beta testing — re-add "participant" to roles to restore.
    { name: "Program Catalog", href: "/catalog", icon: GraduationCap, roles: ["advisor", "program_admin", "content_admin", "admin"], showAlways: true },
    // TODO: Directory feature is unfinished — re-add nav entry once complete (route/page/backend remain wired up).
    { name: "Audit Programs", href: "/audit", icon: ShieldCheck, roles: ["admin"], showAlways: false, showForAudit: true },
    { name: "Advisor Dashboard", href: "/advisor", icon: Users, roles: ["advisor", "program_admin", "content_admin", "admin"], showAlways: false },
    { name: "Assessment Management", href: "/admin/assessments", icon: ClipboardList, roles: ["content_admin", "admin"], showAlways: false },
    { name: "User Management", href: "/admin/users", icon: Users, roles: ["program_admin", "content_admin", "admin"], showAlways: false },
    { name: "Program Management", href: "/admin/programs", icon: LayoutDashboard, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Archived Teams", href: "/admin/archived-teams", icon: History, roles: ["program_admin", "content_admin", "admin"], showAlways: false },
    { name: "Promo Codes", href: "/admin/promo-codes", icon: CreditCard, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Content Builder", href: "/admin/content", icon: PenTool, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Program Templates", href: "/admin/templates", icon: Copy, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Markdown Guide", href: "/admin/markdown-guide", icon: BookOpen, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Email Management", href: "/admin/emails", icon: Mail, roles: ["admin"], showAlways: false },
    { name: "Program Pathways", href: "/admin/pathways", icon: GitBranch, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Logs", href: "/admin/user-logs", icon: FileText, roles: ["admin"], showAlways: false },
    { name: "Certifications", href: "/admin/certifications", icon: Award, roles: ["content_admin", "admin"], showAlways: false },
    { name: "Admin Tools", href: "/admin/tools", icon: Wrench, roles: ["admin"], showAlways: false },
    { name: "Conversations", href: "/conversations", icon: MessageSquare, roles: ["advisor", "program_admin", "content_admin", "admin"], showAlways: true },
    { name: "Support Center", href: "/support", icon: LifeBuoy, roles: ["participant", "advisor", "program_admin", "content_admin", "admin"], showAlways: true },
    { name: "Settings", href: "/profile", navigateTo: "/profile?tab=settings", icon: Settings, roles: ["participant", "advisor", "program_admin", "content_admin", "admin"], showAlways: true },
  ];

  const navigation = allNavigation.filter(item => {
    if (item.showForAudit) {
      return hasAuditAccess;
    }
    return user?.role && item.roles.includes(user.role);
  });

  const NavContent = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      <div className={cn("px-6 pt-6", collapsed && "px-4 pt-4")}>
        <div className={cn("flex items-center gap-3 mb-8", collapsed && "justify-center mb-6")}>
          {collapsed ? (
            <img
              src={soulMark}
              alt="Soul Living"
              className="w-10 h-10 object-contain flex-shrink-0"
            />
          ) : (
            <img 
              src={soulLogo} 
              alt="Soul Living" 
              className="h-9 w-auto brightness-0 invert"
            />
          )}
        </div>
      </div>

      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto px-6 pb-6 sidebar-scroll",
          collapsed && "px-4 pb-4",
        )}
      >
        <nav className="space-y-1.5" aria-label="Main navigation">
          <TooltipProvider delayDuration={0}>
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href)) || (!!item.navigateTo && location.startsWith(item.href));
              
              const isSupport = item.name === "Support Center";
              const isConvos = item.name === "Conversations";
              const isLogs = item.name === "Logs";
              const itemBadge = isConvos ? unreadCount : isSupport ? supportUnread : isLogs ? alertsUnreadCount : 0;
              const showBadge = itemBadge > 0;
              
              const navItem = (
                <Link
                  key={item.name}
                  href={item.navigateTo ?? item.href}
                  onClick={handleNavClick}
                  data-testid={`nav-link${item.href.replace(/\//g, "-")}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group cursor-pointer relative no-underline",
                    isActive
                      ? "bg-sidebar-primary text-white shadow-md"
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-white",
                    collapsed && "justify-center px-2"
                  )}
                >
                  <span className="relative">
                    <Icon className={cn("w-4 h-4 flex-shrink-0", isActive ? "text-white" : "text-sidebar-foreground/50 group-hover:text-white")} />
                    {showBadge && collapsed && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid={isSupport ? "support-unread-badge-collapsed" : "unread-badge-collapsed"}>
                        {itemBadge > 99 ? "99+" : itemBadge}
                      </span>
                    )}
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {showBadge && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5" data-testid={isSupport ? "support-unread-badge" : "unread-badge"}>
                          {itemBadge > 99 ? "99+" : itemBadge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.name}>
                    <TooltipTrigger asChild>
                      {navItem}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return navItem;
            })}
          </TooltipProvider>
        </nav>
      </div>

      <div className={cn("mt-auto p-6 border-t border-sidebar-border", collapsed && "p-4")}>
        <TooltipProvider delayDuration={0}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setSupportDialogOpen(true)}
                  className="w-full flex items-center justify-center py-2 text-sidebar-foreground/70 hover:text-white cursor-pointer transition-colors"
                  data-testid="button-footer-new-ticket-collapsed"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                Submit a ticket
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={() => setSupportDialogOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2 text-sidebar-foreground/70 hover:text-white cursor-pointer text-sm font-medium transition-colors"
              data-testid="button-footer-new-ticket"
            >
              <HelpCircle className="w-4 h-4" />
              Submit a ticket
            </button>
          )}
        </TooltipProvider>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Skip to main content — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[200] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <aside
        aria-label="Main navigation"
        className={cn(
          "hidden md:block border-r border-border fixed inset-y-0 z-50 transition-all duration-300",
          isCollapsed ? "w-[72px]" : "w-64"
        )}
      >
        <NavContent collapsed={isCollapsed} />
        
        {/* Collapse/Expand Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            "absolute -right-3 top-7 h-6 w-6 rounded-full border bg-background shadow-md hover:bg-accent z-50",
            "flex items-center justify-center"
          )}
          data-testid="button-toggle-sidebar"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </Button>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 border-r-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <NavContent collapsed={false} />
        </SheetContent>
      </Sheet>

      <div className={cn(
        "flex-1 flex flex-col min-h-screen transition-all duration-300 overflow-x-hidden",
        isCollapsed ? "md:ml-[72px]" : "md:ml-64"
      )}>
        {/* Header */}
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-40 px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open navigation menu"
              data-testid="button-open-mobile-nav"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="hidden md:flex items-center text-sm text-muted-foreground">
              <Link
                href="/"
                className="hover:text-foreground transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="link-breadcrumb-home"
              >
                Home
              </Link>
              {location !== "/" && (
                <>
                  <ChevronRight className="w-4 h-4 mx-2" />
                  <span className="font-medium text-foreground capitalize">
                    {location.split('/')[1]?.replace('-', ' ') || 'Page'}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <HeaderSearch />

            <Button
              variant="ghost"
              size="icon"
              className="relative text-muted-foreground hover:text-foreground"
              aria-label="Go to Support Center"
              data-testid="button-header-help"
              onClick={() => setLocation("/support")}
            >
              <HelpCircle className="w-5 h-5" />
              {supportUnread > 0 && (
                <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center border-2 border-card" data-testid="header-support-badge">
                  {supportUnread > 9 ? "9+" : supportUnread}
                </span>
              )}
            </Button>

            <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-muted-foreground hover:text-foreground"
                  aria-label="Notifications"
                  data-testid="button-notifications"
                >
                  <Bell className="w-5 h-5" />
                  {bellTotal > 0 && (
                    <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center border-2 border-card" data-testid="bell-badge">
                      {bellTotal > 99 ? "99+" : bellTotal}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notifications-dropdown">
                <div className="px-4 py-3 border-b">
                  <p className="text-sm font-semibold">Notifications</p>
                </div>

                {/* Support Tickets */}
                <div className="px-4 py-3 border-b">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <LifeBuoy className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Support Tickets</span>
                    </div>
                    {supportUnread > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notif-support-badge">
                        {supportUnread > 99 ? "99+" : supportUnread}
                      </span>
                    )}
                  </div>
                  {supportUnread > 0 ? (
                    <p className="text-sm text-foreground">
                      {supportUnread} unread {supportUnread === 1 ? "ticket" : "tickets"}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">All caught up</p>
                  )}
                  <Link
                    href="/support"
                    onClick={() => setNotifOpen(false)}
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                    data-testid="notif-support-link"
                  >
                    View all →
                  </Link>
                </div>

                {/* Messages */}
                <div className={cn("px-4 py-3", isStaff && "border-b")}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Messages</span>
                    </div>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notif-messages-badge">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 ? (
                    <p className="text-sm text-foreground">
                      {unreadCount} unread {unreadCount === 1 ? "message" : "messages"}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">All caught up</p>
                  )}
                  <Link
                    href="/conversations"
                    onClick={() => setNotifOpen(false)}
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                    data-testid="notif-messages-link"
                  >
                    View all →
                  </Link>
                </div>

                {/* System Alerts — staff only (advisor + admin) */}
                {isStaff && (
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">System Alerts</span>
                      </div>
                      {alertsUnreadCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notif-alerts-badge">
                          {alertsUnreadCount > 99 ? "99+" : alertsUnreadCount}
                        </span>
                      )}
                    </div>
                    {recentAlerts?.alerts && recentAlerts.alerts.length > 0 ? (
                      <ul className="space-y-1 mb-1">
                        {recentAlerts.alerts.slice(0, 5).map((alert) => (
                          <li key={alert.id} className="text-sm text-foreground truncate" data-testid={`notif-alert-${alert.id}`}>
                            {alert.message}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">All caught up</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <Link
                        href="/admin/user-logs"
                        onClick={() => setNotifOpen(false)}
                        className="text-xs text-primary hover:underline inline-block"
                        data-testid="notif-alerts-link"
                      >
                        View all →
                      </Link>
                      {alertsUnreadCount > 0 && (
                        <button
                          type="button"
                          onClick={markAlertsRead}
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          data-testid="button-mark-alerts-read"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-3 cursor-pointer pl-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label="Open account menu"
                  data-testid="button-account-menu"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold text-foreground leading-none">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 capitalize">
                      {user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                  <Avatar className="h-9 w-9 border-2 border-background shadow-sm">
                    <AvatarFallback>
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setLocation('/profile')}>Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <main id="main-content" className="flex-1 p-6 md:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500 slide-in-from-bottom-4">
          {children}
        </main>
      </div>

      <SupportTicketDialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen} />
    </div>
  );
}
